import { useEffect, useRef, useState } from "react";
import { Pencil } from "lucide-react";

/**
 * Branded prompt modal — replaces every `window.prompt()` in the app.
 *
 * Usage :
 *   <PromptDialog
 *     open={state}
 *     title="Nouveau nom"
 *     placeholder="Accueil, Caisse..."
 *     defaultValue={currentName}
 *     maxLength={20}
 *     confirmLabel="OK"
 *     onConfirm={(value) => save(value)}
 *     onCancel={() => setState(false)}
 *   />
 */
export default function PromptDialog({
  open,
  title = "Saisir une valeur",
  message,
  placeholder = "",
  defaultValue = "",
  maxLength = 100,
  confirmLabel = "OK",
  cancelLabel = "Annuler",
  busy = false,
  uppercase = false,
  onConfirm,
  onCancel,
  testid = "prompt-dialog",
}) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Reset only when the modal OPENS — re-renders of the parent (e.g.
    // polling that refreshes queues every few seconds) must NOT wipe the
    // text the user is currently typing. We intentionally ignore further
    // changes to `defaultValue`, `busy`, and `onCancel` references.
    setValue(defaultValue);
    const t = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const submit = () => {
    const v = (value || "").trim();
    if (!v) return;
    onConfirm?.(v);
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in"
      onClick={() => !busy && onCancel?.()}
      data-testid={`${testid}-backdrop`}
    >
      <div
        className="bg-white border-2 border-ink max-w-md w-full rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby={`${testid}-title`}
        data-testid={testid}
      >
        <div className="px-5 py-4 border-b border-border bg-brand/5 flex items-center gap-3">
          <Pencil size={20} className="text-brand shrink-0" />
          <h3 id={`${testid}-title`} className="font-display text-lg font-black text-ink">
            {title}
          </h3>
        </div>
        <div className="px-5 py-5 text-sm text-ink leading-relaxed space-y-3">
          {message && <p className="text-subtle">{message}</p>}
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => {
              const next = uppercase ? e.target.value.toUpperCase() : e.target.value;
              setValue(next.slice(0, maxLength));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); submit(); }
            }}
            maxLength={maxLength}
            placeholder={placeholder}
            className={`w-full border border-border rounded-md h-11 px-3 text-base font-medium text-ink focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand ${uppercase ? "uppercase" : ""}`}
            data-testid={`${testid}-input`}
          />
          <p className="text-[10px] font-mono text-subtle text-right">{value.length} / {maxLength}</p>
        </div>
        <div className="px-5 py-4 bg-surface/50 border-t border-border flex flex-wrap justify-end gap-2">
          <button
            disabled={busy}
            onClick={onCancel}
            className="inline-flex items-center justify-center h-9 px-4 rounded-md border border-ink text-ink text-xs font-bold uppercase tracking-wider disabled:opacity-50"
            data-testid={`${testid}-cancel-btn`}
          >
            {cancelLabel}
          </button>
          <button
            disabled={busy || !value.trim()}
            onClick={submit}
            className="inline-flex items-center justify-center h-9 px-5 rounded-md bg-brand text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50 hover:bg-brand-hover"
            data-testid={`${testid}-confirm-btn`}
          >
            {busy ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
